import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';
import DataState from 'app/src/components/DataState';
import { Text } from 'app/src/styles';

import { TXNS, CREATE_TXN } from '../Txns/queries';
import METHODS from '../Txns/methods';
import { specialAccount, prepareTxnInput } from '../Txns/utils';

export const FIELDS = {
  bankDate: {
    label: 'Date',
    type: 'date',
    rules: { required: true },
  },
  balanceAccountId: {
    label: 'Payment Account',
    type: 'account',
    rules: { required: true },
    preferredTypes: [
      'Bank', 'Accounts Payable', 'Credit Card', 'Other Current Asset'
    ],
    preferredOnly: true,
  },
  method: {
    label: 'Payment Method',
    select: METHODS,
    blank: 'null',
  },
  addToAchBatch: {
    label: 'Add to current ACH Batch?',
    type: 'boolean',
    disabled: true,
  },
  reference: {
    label: 'Invoice/Reference #',
  },
  name: {
    type: 'entityName',
    preferredTypes: ['Vendor'],
  },
  memo: {},
};

export const LINE_FIELDS = {
  accountId: {
    label: 'Account',
    type: 'account',
    preferredTypes: ['Expense', 'Cost of Goods Sold'],
  },
  debitLessCredit: {
    label: 'Amount',
  },
  assetId: {
    label: 'Asset',
    type: 'asset',
    createFee: true,
    width: 140,
    extraLabels: {
      saleAssetId: 'Lot/Sale',
      feeId: 'Fee',
    },
  },
  memo: { },
};

export const EMPTY_LINE = {
  accountId: '',
  debitLessCredit: '',
  memo: '',
};

const NewExpense = ({ setShowModal, refetch, type, asset }) => {
  const { accounts, paymentTerms } = useContext(AuthContext);

  const emptyLines = [{...EMPTY_LINE, assetId: asset?.id}];
  const [nestedResources, setNestedResources] = useState(emptyLines);
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: { bankDate: DateTime.now().toISODate() },
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [createTxn] = useMutation(CREATE_TXN, {
    onCompleted: data => {
      if (data.createTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.createTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    const missingExpenses = input.txnLinesAttributes.filter(txnLine => (
      txnLine.feeId && (!txnLine.accountId || !txnLine.debitLessCredit)
    ));

    if (missingExpenses.length > 0) {
      setMutationErrors([
        {
          path: [''],
          message: 'Expense lines with an fee chosen must have an ' +
            'expense amount and account set.'
        }
      ]);

      return;
    }

    const nonZeroLines = input.txnLinesAttributes.filter(txnLine => (
      txnLine.debitLessCredit && parseFloat(txnLine.debitLessCredit)
    ));

    if (0 === nonZeroLines.length) {
      setMutationErrors([
        {
          path: [''],
          message: 'No lines have an amount set.'
        }
      ]);

      return;
    }

    setFormStatus('submitting');
    input.type = 'Expense';

    if ('Bill' === type) {
      input.balanceAccountId =
        specialAccount({ accounts, special: 'ap' }).value;

      input.method = null;
    } else if ('Credit Card Charge' === type) {
      input.method = 'Credit Card';
    } else if ('Internal Expense' === type) {
      input.balanceAccountId =
        specialAccount({ accounts, special: 'internal_expense' }).value;

      input.method = null;
    }

    createTxn({ variables: prepareTxnInput(input, fields) });
  });

  const watchAll = watch();

  const addLine = () => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;
    const lastLine = txnLinesWatch[txnLinesWatch.length - 1];
    const prefix = `txnLinesAttributes.${nestedResources.length}`;

    register(`${prefix}.accountId`, { value: '' });
    register(`${prefix}.debitLessCredit`, { value: '' });
    register(`${prefix}.memo`, { value: '' });
    register(`${prefix}.assetId`, { value: lastLine.assetId });
    register(`${prefix}.saleAssetId`, { value: lastLine.saleAssetId });
    setNestedResources([...nestedResources, EMPTY_LINE]);
  };

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.debitLessCredit) {
        const prefix = `txnLinesAttributes.${nestedResources.length}`;

        register(`${prefix}.accountId`, { value: '' });
        register(`${prefix}.debitLessCredit`, { value: '' });
        register(`${prefix}.memo`, { value: '' });
        register(`${prefix}.assetId`, { value: lastLine.assetId });
        register(`${prefix}.saleAssetId`, { value: lastLine.saleAssetId });
        setNestedResources([...nestedResources, EMPTY_LINE]);
      }
    }
  }, [watchAll]);

  let fields = { ...FIELDS };
  const lineFields = { ...LINE_FIELDS };

  if (watchAll) {
    if (watchAll.addToAchBatch || 'ACH' === watchAll.method) {
      if (fields.addToAchBatch.disabled) {
        fields.addToAchBatch = { ...fields.addToAchBatch, disabled: false };
      }
    } else {
      if (!fields.addToAchBatch.disabled) {
        fields.addToAchBatch = { ...fields.addToAchBatch, disabled: true };
      }
    }
  }

  if ('Bill' === type) {
    delete fields.balanceAccountId;
    delete fields.method;

    fields.reference = {
      ...fields.reference,
      label: 'Invoice #',
    };

    fields.paymentTermId = {
      label: 'Payment Terms',
      select: (paymentTerms || []),
      rules: { required: false },
      blank: 'null',
    };
  } else if ('Credit Card Charge' === type) {
    fields.balanceAccountId = {
      ...fields.balanceAccountId,
      preferredTypes: ['Credit Card'],
      label: 'Credit Card Account',
    };

    delete fields.method;
  } else if ('Internal Expense' === type) {
    delete fields.balanceAccountId;
    delete fields.method;

    fields.reference = {
      ...fields.reference,
      label: 'Reference #',
    };
  }

  return (
    <View>
      <FormProvider watch={watch}>
        <Fields
          fields={fields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <NestedFields
          prefix="txnLinesAttributes"
          resources={nestedResources}
          fields={lineFields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <Pressable onPress={addLine}>
          <Text style={{ fontSize: 14 }}>+ Add Line</Text>
        </Pressable>

        <MutationErrors errors={mutationErrors} />

        <ButtonGroup>
          <Button
            label={`Save ${type}`}
            onPress={handleSubmit(onSubmit)}
            wide
          />
        </ButtonGroup>
      </FormProvider>
    </View>
  );
};

export default NewExpense;
